import {
  DataGridPremium,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  gridClasses
} from "@mui/x-data-grid-premium";
import Grid from "@mui/material/Unstable_Grid2";
import {
  UserNotifications,
  showToastForBulkResult,
  useOpenSearchUsers
} from "./userUtils";
import { useTranslation } from "react-i18next";
import {
  ProgramRoleSelectFields,
  UserNetworkErrors
} from "../../app/constants";
import { DataGridErrorOverlay } from "../../common-ui/DataGridErrorOverlay";
import { useEffect, useState, useMemo } from "react";
import {
  InstitutionNetworkErrors,
  useAllInstitutions
} from "../Institutions/institutionUtils";
import { useAppSelector } from "../../app/hooks";
import {
  selectInstitutions,
  selectInstitutionsError,
  selectInstitutionsStatus
} from "../Institutions/institutionsSlice";
import { selectCurrentUser } from "./usersSlice";
import { UserTableProps } from "../../app/types";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from "@mui/material";
import { ProgramMembership, ProgramRole } from "../../API";
import { alphabetizePrograms, useAllPrograms } from "../Programs/programUtils";
import {
  GroupAdd,
  PersonAdd,
  ScheduleSendRounded
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  setShowToast,
  setToastAutoHideDuration,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { useDispatch } from "react-redux";
import { useBulkResendUserInvitations } from "../UserInvitations/userInvitationUtils";
import {
  setModalBoxStyle,
  setModalContent,
  setModalTitle,
  setShowModal
} from "../../common-ui/modalSlice";
import PgyDeltaInput from "./PgyDeltaInput";
import { bulkUpdatePgy } from "./usersAPI";
import { ProgramUser } from "../../custom-types";
import { uniqBy } from "lodash";

type SysAdminsUsersTableProps = UserTableProps;

export const ProgramRoleSelectFieldsNew = [
  { label: "Clinician Trainee", value: "Trainee" },
  { label: "Clinician Non Trainee", value: "Attending" },
  { label: "Program Administrator", value: ProgramRole.ProgramAdmin },
  {
    label: "Institutional Administrator",
    value: ProgramRole.Admin
  }
];

export const SysAdminsUsersTable = ({
  disableVirtualization = false,
  columns,
  selectedProgramId,
  setSelectedProgramId,
  handleRowClick
}: SysAdminsUsersTableProps) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);

  const { programs } = useAllPrograms();

  const [selectedRole, setSelectedRole] = useState<ProgramRole | string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const [pgyBumpValue, setPgyBumpValue] = useState<number | undefined>(1);
  const [pgyModalInvocations, setPgyModalInvocations] = useState(0);

  const [refetchUsers, setRefetchUsers] = useState<number>(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    status: resendInvitationsStatus,
    successCount,
    currentStep,
    bulkResendUserInvitations
  } = useBulkResendUserInvitations();

  // Always use server path; remove programId client fetch

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "firstName", sort: "asc" }
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: []
  });

  // Status is forwarded as a top-level AND clause; no need to inject into filterModel

  // Use Redux institutions (pre-loaded for SysAdmins in auth flow)
  // For SysAdmins, institutions are already loaded in auth flow, so just use Redux
  const institutions = useAppSelector(selectInstitutions);
  const institutionsStatus = useAppSelector(selectInstitutionsStatus);
  const institutionsError = useAppSelector(selectInstitutionsError);
  
  // Memoize empty arrays to prevent creating new references on every render
  const memoizedInstitutions = useMemo(() => institutions || [], [institutions]);
  const memoizedPrograms = useMemo(() => programs || [], [programs]);
  
  const { users, status, error, total, nextPage, prevPage } =
    useOpenSearchUsers(
      paginationModel.pageSize,
      sortModel,
      filterModel,
      memoizedInstitutions,
      memoizedPrograms,
      refetchUsers,
      selectedProgramId || undefined,
      selectedRole || undefined,
      selectedStatus || undefined,
      false
    );

  const enrichUser = (user: ProgramUser): ProgramUser => {
    const programMemberships = user.programMemberships?.items || [];
    const activeMemberships = programMemberships.filter((m) => !m?.endDate);

    const rawRoles = activeMemberships
      .map((membership) => membership?.programRole)
      .filter((r): r is ProgramRole => !!r); // Type narrowing

    const roleSet = new Set<string>();
    let hasClinician = false;
    rawRoles.forEach((role) => {
      if (role === "Trainee" || role === "Attending") {
        hasClinician = true;
      } else {
        roleSet.add(role);
      }
    });
    if (hasClinician) roleSet.add("Clinician");

    const trainingPrograms = programs
      .filter((p) =>
        activeMemberships.some(
          (m) => m?.programId === p.id && m.programRole === "Trainee"
        )
      )
      .map((p) => p.name)
      .join(", ");

    const institutionName = programs
      .filter((p) => activeMemberships.some((m) => m?.programId === p.id))
      .map((p) => p.institution?.name)
      .join(", ");

    const programName =
      programs.find((p) => activeMemberships.some((m) => m?.programId === p.id))
        ?.name ?? "N/A";

    // Job title calculation rules:
    // 1) If a jobTitle exists from backend, use it.
    // 2) Else, use the raw backend role if present (Attending, Trainee, Program Admin), otherwise blank.
    const hasBackendTrainee = activeMemberships.some(
      (m) => m?.programRole === "Trainee"
    );
    const hasBackendAttending = activeMemberships.some(
      (m) => m?.programRole === "Attending"
    );
    const hasBackendProgramAdmin = activeMemberships.some(
      (m) => m?.programRole === ProgramRole.ProgramAdmin
    );
    const backendRoleForJobTitle =
      (hasBackendTrainee && "Trainee") ||
      (hasBackendAttending && "Attending") ||
      (hasBackendProgramAdmin && "Program Admin") ||
      undefined;

    const computedJobTitle = (
      user.jobTitle && String(user.jobTitle).trim().length > 0
        ? String(user.jobTitle).trim()
        : backendRoleForJobTitle
    ) as string | undefined;

    return {
      ...(user as any),
      role: Array.from(roleSet).join(", "),
      rawRoles: programMemberships
        .filter(
          (m): m is ProgramMembership & { programRole: ProgramRole } =>
            !!m?.programRole && !m.endDate
        )
        .map((m) => m.programRole),
      trainingProgram: trainingPrograms,
      institutionName,
      program: programName,
      jobTitle: computedJobTitle,
      status: user.eulaAcceptedDate ? "Active" : "Unregistered"
    };
  };

  const queriedUsers = (() => {
    const enriched = ((users as ProgramUser[]) || [])
      .map(enrichUser)
      .filter(Boolean);
    return enriched;
  })();

  const filteredUsers = queriedUsers;

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (!selectedProgramId) {
      if (model.page > paginationModel.page) {
        nextPage();
      }
      if (model.page < paginationModel.page) {
        prevPage();
      }
    }
    setPaginationModel(model);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
  };

  const anyApiIsLoading = [
    institutionsStatus,
    status,
    resendInvitationsStatus
  ].includes("loading");

  useEffect(() => {
    if (resendInvitationsStatus === "idle") return;

    if (resendInvitationsStatus === "loading") {
      dispatch(setToastStyle("info"));
      dispatch(
        setToastMessage(
          t(
            `Resending invitation ${currentStep} of ${selectedUserIds.length}...`
          )
        )
      );
      dispatch(setToastAutoHideDuration(null));
      dispatch(setShowToast(true));
    }

    if (resendInvitationsStatus === "succeeded") {
      const style =
        successCount === selectedUserIds.length ? "success" : "warning";
      let message = `Invitation has been re-sent successfully to ${successCount} ${
        successCount === 1 ? "person" : "people"
      }`;
      if (successCount < selectedUserIds.length) {
        message += ` out of ${selectedUserIds.length} selected. Please contact your administrator for further assistance.`;
      }
      dispatch(setToastStyle(style));
      dispatch(setToastMessage(t(message)));
      dispatch(setToastAutoHideDuration(8000));
    }

    if (resendInvitationsStatus === "failed") {
      dispatch(setToastStyle("error"));
      dispatch(
        setToastMessage(
          t(
            "An error ocurred re-sending the invitations. Please contact your administrator for further assistance."
          )
        )
      );
    }
    dispatch(setShowToast(true));
  }, [resendInvitationsStatus, successCount, currentStep]);

  // Removed programId client-fetch effect

  // When switching from program-selected (client mode) back to all programs (server mode),
  // reset the grid to the first page so the server fetch starts at page 1.
  useEffect(() => {
    if (!selectedProgramId) {
      setPaginationModel((prev) => ({
        page: 0,
        pageSize: prev.pageSize
      }));
    }
  }, [selectedProgramId]);

  useEffect(() => {
    const selectedUsers = selectedUserIds
      .map((userId) =>
        filteredUsers.find((user) => user.id === userId && !!user.pgy)
      )
      .filter(Boolean) as typeof filteredUsers;

    dispatch(
      setModalContent(
        <Box
          sx={{ display: "flex", flexDirection: "column" }}
          alignItems="center"
          gap={3}
          padding={3}
        >
          <Box>
            You are updating the PGY by{" "}
            <PgyDeltaInput
              setDeltaValue={setPgyBumpValue}
              currentDeltaValue={pgyBumpValue}
            />{" "}
            for {selectedUsers.length} people
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!pgyBumpValue}
            onClick={() => {
              if (!pgyBumpValue) return;

              const mutationStatements = selectedUsers
                .map((user) => ({
                  id: user.id,
                  pgy: (user.pgy || 0) + pgyBumpValue
                }))
                .filter(
                  (statement) => statement.pgy >= 1 && statement.pgy <= 12
                );

              if (mutationStatements.length === 0) {
                dispatch(setToastStyle("error"));
                dispatch(
                  setToastMessage(
                    t(
                      "This PGY change can not be performed on any of the selected users, the final PGYs might be out of range"
                    )
                  )
                );
                dispatch(setShowToast(true));
                return;
              }

              bulkUpdatePgy(mutationStatements).then((bulkResult) => {
                showToastForBulkResult(
                  dispatch,
                  bulkResult,
                  selectedUsers.length,
                  t(
                    `You have successfully updated the PGY for ${
                      selectedUsers.length
                    } ${selectedUsers.length > 1 ? "users" : "user"}`
                  ),
                  t(
                    `PGY has been successfully updated for ${bulkResult.fulfilled} out of ${selectedUsers.length} users`
                  ),
                  t(
                    "An error ocurred updating the PGYs, please try again later"
                  )
                );
                setTimeout(() => setRefetchUsers(refetchUsers + 1), 500);
              });
              dispatch(setShowModal(false));
            }}
          >
            CONFIRM
          </Button>
        </Box>
      )
    );
    dispatch(setModalBoxStyle({ width: 444, minWidth: 444 }));
    if (pgyModalInvocations > 0) dispatch(setShowModal(true));
  }, [pgyModalInvocations, pgyBumpValue]);

  return (
    <Grid
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      spacing={2}
      gap={2}
    >
      {(status === "failed" || error) && (
        <DataGridErrorOverlay
          errorMessage={UserNetworkErrors.FetchAllUsersError}
        />
      )}
      {(institutionsError === "failed" || institutionsError) && (
        <DataGridErrorOverlay
          errorMessage={InstitutionNetworkErrors.FetchAllInstitutionsError}
        />
      )}
      <Grid xs={12} pt={2}>
        <Grid xs={12} container spacing={2}>
          <Grid>
            <FormControl>
              <InputLabel shrink id="program-label">
                {t("Program")}
              </InputLabel>
              <Select
                labelId="program-label"
                label={t("Programs")}
                id="programSelection"
                style={{ width: 300 }}
                size="small"
                displayEmpty
                value={selectedProgramId || ""}
                input={<OutlinedInput notched label={t("Programs")} />}
                onChange={(event) => {
                  if (setSelectedProgramId) {
                    setSelectedProgramId(event.target.value);
                  }
                }}
              >
                <MenuItem value={""} key={"no-program"}>
                  All
                </MenuItem>
                {alphabetizePrograms(programs, true)?.map((program) => (
                  <MenuItem value={program.id} key={program.id}>
                    {`${program.institution?.name} (Institution) > ${program.name} (Program)`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl>
              <InputLabel shrink id="role-label">
                {t("Role")}
              </InputLabel>
              <Select
                disabled={!selectedProgramId}
                labelId="role-label"
                id="roleSelection"
                label={t("Role")}
                style={{ width: 300 }}
                size="small"
                displayEmpty
                value={selectedRole || ""}
                input={<OutlinedInput notched label={t("Role")} />}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "") {
                    setSelectedRole(value);
                  } else {
                    setSelectedRole(value as ProgramRole);
                  }
                }}
              >
                <MenuItem value="">All</MenuItem>
                {ProgramRoleSelectFieldsNew?.map((roleSelectField) => (
                  <MenuItem
                    value={roleSelectField.value}
                    key={roleSelectField.value}
                  >
                    {roleSelectField.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl>
              <InputLabel shrink id="status-label">
                {t("Status")}
              </InputLabel>
              <Select
                labelId="status-label"
                id="statusSelection"
                label={t("Status")}
                style={{ width: 300 }}
                size="small"
                displayEmpty
                value={selectedStatus || ""}
                input={<OutlinedInput notched label={t("Status")} />}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedStatus(value);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {[
                  { value: "Active", label: "Active" },
                  { value: "Unregistered", label: "Unregistered" }
                ]?.map((roleSelectField) => (
                  <MenuItem
                    value={roleSelectField.value}
                    key={roleSelectField.value}
                  >
                    {roleSelectField.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      <Grid xs={12} container spacing={2}>
        <Grid>
          <Button
            disabled={anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<PersonAdd />}
            onClick={() => navigate("/invitations/new")}
          >
            {t("NEW USER")}
          </Button>
        </Grid>

        <Grid>
          <Button
            disabled={anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<GroupAdd />}
            onClick={() => navigate("/invitations/bulkupload")}
          >
            {t("BULK NEW USERS")}
          </Button>
        </Grid>

        <Grid>
          <LoadingButton
            disabled={selectedUserIds.length === 0 || anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<ScheduleSendRounded />}
            loading={resendInvitationsStatus === "loading"}
            loadingPosition="start"
            onClick={() => {
              bulkResendUserInvitations(selectedUserIds);
            }}
          >
            {t("RESEND INVITATION")}
          </LoadingButton>
        </Grid>

        <Grid>
          <Button
            disabled={selectedUserIds.length === 0 || anyApiIsLoading}
            variant="contained"
            size="small"
            onClick={() => {
              const selectedUsers = selectedUserIds
                .map((userId) =>
                  filteredUsers.find((user) => user.id === userId && !!user.pgy)
                )
                .filter(Boolean) as typeof filteredUsers;

              if (!selectedUsers?.length) {
                dispatch(
                  setToastMessage(t("There are no applicable users selected"))
                );
                dispatch(setToastStyle("error"));
                dispatch(setShowToast(true));
                return;
              }

              setPgyModalInvocations(pgyModalInvocations + 1);
            }}
          >
            {t("+1 PGY")}
          </Button>
        </Grid>

        {/* Create a modal to show that report generation has been initiated w/ email message */}
      </Grid>

      <Grid xs={12} sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <DataGridPremium
          disableVirtualization={disableVirtualization}
          checkboxSelection
          aria-label="users-table-sysadmins"
          rows={filteredUsers || []}
          columns={columns}
          loading={anyApiIsLoading}
          rowCount={total}
          localeText={{
            noRowsLabel: t(UserNotifications.NoUsers)
          }}
          onRowSelectionModelChange={(selection) => {
            setSelectedUserIds(selection as string[]);
          }}
          paginationMode={"server"}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortingMode={"server"}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterMode={"server"}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onRowClick={handleRowClick}
          getRowHeight={() => "auto"}
          sx={{
            flex: 1,
            minHeight: 0,
            [`& .${gridClasses.cell}`]: {
              py: 1,
              whiteSpace: "pre-wrap"
            }
          }}
        />
      </Grid>
    </Grid>
  );
};
